<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <h4 class="card-title">Lista de Pacientes</h4>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end">
                  <!-- <router-link to="/patients/create" class="btn btn-success">
                    <i class="ri-user-add-line me-1"></i> Novo Paciente
                  </router-link> -->
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-sm-12 col-md-6">
                <div id="patients-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Mostrar&nbsp;
                    <b-form-select v-model="perPage" size="sm" :options="pageOptions"></b-form-select>&nbsp;registros
                  </label>
                </div>
              </div>
              <!-- Search -->
              <div class="col-sm-12 col-md-6">
                <div id="patients-table_filter" class="dataTables_filter text-md-right">
                  <label class="d-inline-flex align-items-center">
                    Buscar:
                    <b-form-input
                      v-model="cpfSearch"
                      type="search"
                      class="form-control form-control-sm ml-2"
                      placeholder="Digite o CPF"
                      @input="formatCpfSearch"
                      @keyup.enter="searchByCpf"
                    ></b-form-input>
                    <button class="btn btn-outline-primary btn-sm ml-2" @click="searchByCpf">
                      <i class="ri-search-line"></i>
                    </button>
                    <button class="btn btn-outline-secondary btn-sm ml-2" @click="clearSearch">
                      <i class="ri-close-line"></i>
                    </button>
                  </label>
                </div>
              </div>
              <!-- End search -->
            </div>

            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
              </div>
            </div>

            <div v-else class="table-responsive">
              <b-table
                :items="patients"
                :fields="fields"
                responsive="sm"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                @filtered="onFiltered"
              >
                <template v-slot:cell(name)="row">
                  {{ row.value || 'N/A' }}
                </template>

                <template v-slot:cell(birth_date)="row">
                  {{ formatDate(row.value) }}
                </template>

                <template v-slot:cell(sex)="row">
                  <span class="badge bg-soft-primary text-primary">{{ getSexLabel(row.value) }}</span>
                </template>

                <template v-slot:cell(cpf)="row">
                  {{ formatCpf(row.value) }}
                </template>

                <template v-slot:cell(sus_card_number)="row">
                  {{ row.value || 'N/A' }}
                </template>

                <template v-slot:cell(created_at)="row">
                  {{ formatDate(row.value) }}
                </template>

                <template v-slot:cell(action)="row">
                  <a
                    href="javascript:void(0);"
                    class="text-info"
                    v-b-tooltip.hover
                    title="Ver Exames"
                    @click="viewExams(row.item)"
                  >
                    <i class="mdi mdi-file-find font-size-18"></i>
                  </a>
                </template>
              </b-table>
            </div>

            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { patientService } from "@/api/patients";

export default {
  page: {
    title: "Lista de Pacientes",
    meta: [{ name: "description", content: "Lista de pacientes cadastrados no sistema" }],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      title: "Lista de Pacientes",
      items: [
        {
          text: "Pacientes",
          href: "/patients",
        },
        {
          text: "Lista",
          active: true,
        },
      ],
      patients: [],
      loading: false,
      cpfSearch: "",
      isSearching: false,
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 25, 50],
      filter: null,
      filterOn: [],
      sortBy: "name",
      sortDesc: false,
      fields: [
        { key: "name", sortable: true, label: "Nome" },
        { key: "birth_date", sortable: true, label: "Data de Nascimento" },
        { key: "sex", sortable: true, label: "Sexo" },
        { key: "cpf", sortable: true, label: "CPF" },
        { key: "sus_card_number", sortable: true, label: "Cartão SUS" },
        { key: "created_at", sortable: true, label: "Criado em" },
        { key: "action", label: "Ações" }
      ]
    };
  },
  computed: {
    /**
     * Total no. of records (from API response)
     */
    rows() {
      return this.totalRows;
    }
  },
  async created() {
    await this.loadPatients();
  },
  watch: {
    // Watch for page changes
    currentPage() {
      if (!this.isSearching) {
        this.loadPatients();
      }
    },
    // Watch for perPage changes
    perPage() {
      this.currentPage = 1; // Reset to first page
      if (!this.isSearching) {
        this.loadPatients();
      }
    }
  },
  methods: {
    async loadPatients() {
      this.loading = true;
      this.isSearching = false;
      try {
        const params = {
          skip: (this.currentPage - 1) * this.perPage,
          limit: this.perPage,
        };
        
        const response = await patientService.listPatients(params);
        
        // A resposta da API tem a estrutura: { data: { patients: [], total: number, skip: number, limit: number } }
        if (response && response.data && response.data.patients && Array.isArray(response.data.patients)) {
          this.patients = response.data.patients;
          this.totalRows = response.data.total || 0;
        } else {
          console.error("Resposta da API inválida:", response);
          this.patients = [];
          this.totalRows = 0;
        }
        
      } catch (error) {
        console.error("Erro ao carregar pacientes:", error);
        this.$toast.error("Erro ao carregar pacientes");
        this.patients = [];
        this.totalRows = 0;
      } finally {
        this.loading = false;
      }
    },

    async searchByCpf() {
      if (!this.cpfSearch.trim()) {
        this.$toast.error("Digite um CPF para buscar");
        return;
      }

      const cpfNumbers = this.cpfSearch.replace(/\D/g, '');
      if (cpfNumbers.length !== 11) {
        this.$toast.error("CPF deve ter 11 dígitos");
        return;
      }

      this.loading = true;
      this.isSearching = true;
      try {
        const response = await patientService.getPatientByCpf(cpfNumbers);
        
        // Verificar se a resposta vem dentro de data ou diretamente
        let patient = null;
        if (response && response.data) {
          patient = response.data;
        } else if (response) {
          patient = response;
        }
        
        if (patient && patient.id) {
          this.patients = [patient];
          this.totalRows = 1;
          this.currentPage = 1;
        } else {
          this.patients = [];
          this.totalRows = 0;
          this.$toast.info("Nenhum paciente encontrado com este CPF");
        }
        
      } catch (error) {
        console.error("Erro ao buscar paciente por CPF:", error);
        if (error.response?.status === 404) {
          this.patients = [];
          this.totalRows = 0;
          this.$toast.info("Nenhum paciente encontrado com este CPF");
        } else {
          this.$toast.error("Erro ao buscar paciente");
        }
      } finally {
        this.loading = false;
      }
    },

    clearSearch() {
      this.cpfSearch = "";
      this.isSearching = false;
      this.currentPage = 1;
      this.loadPatients();
    },

    formatCpfSearch() {
      let value = this.cpfSearch.replace(/\D/g, '');
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      this.cpfSearch = value;
    },

    /**
     * Search the table data with search input
     */
    onFiltered(filteredItems) {
      // Para busca local, ajustar o total de linhas
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    viewPatient(patient) {
      this.$router.push(`/patients/${patient.id}`);
    },

    editPatient(patient) {
      this.$router.push(`/patients/${patient.id}/edit`);
    },

    viewExams(patient) {
      this.$router.push(`/exams/by-patient/${patient.id}`);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('pt-BR');
    },

    formatCpf(cpf) {
      if (!cpf) return 'N/A';
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    },

    getSexLabel(sex) {
      const labels = {
        M: 'Masculino',
        F: 'Feminino',
        O: 'Outro',
      };
      return labels[sex] || sex;
    },
  },
};
</script>
